import React, { useCallback, useEffect, useState } from 'react';
import { AppLayout } from '@/components/AppLayout';
import { BAILLEUR_LINKS } from './links';
import { bailleurApi, downloadPdfWithAuth } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { TID } from '@/constants/testIds';
import { CheckCircle2, Download, Droplets, Plus, Zap } from 'lucide-react';
import { Loader } from '@/components/Loader';

const initialForm = {
  locataire_id: '',
  type: 'eau',
  montant: '',
  devise: 'USD',
  periode: '',
  reference: '',
  date_echeance: '',
  statut: 'a_payer',
  note: '',
};

function FactureDialog({ locataires, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initialForm);
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!form.locataire_id || !form.montant || !form.periode || !form.date_echeance) {
      toast.error('Complétez tous les champs obligatoires.');
      return;
    }
    setSaving(true);
    try {
      await bailleurApi.createFactureService({
        ...form,
        montant: Number(form.montant),
        reference: form.reference.trim() || null,
        note: form.note.trim() || null,
      });
      toast.success('Facture créée avec succès.');
      setForm(initialForm);
      setOpen(false);
      onCreated();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Impossible de créer la facture.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button disabled={locataires.length === 0} className="bg-[#1677FF] hover:bg-[#0E62DB]">
          <Plus className="mr-2 h-4 w-4" /> Créer une facture
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Nouvelle facture d’eau ou d’électricité</DialogTitle>
            <DialogDescription>Enregistrez la consommation facturée à un locataire et suivez son paiement.</DialogDescription>
          </DialogHeader>

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>Locataire *</Label>
              <Select value={form.locataire_id} onValueChange={(value) => set('locataire_id', value)}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Choisir un locataire" /></SelectTrigger>
                <SelectContent>
                  {locataires.map((locataire) => (
                    <SelectItem key={locataire.id} value={locataire.id}>
                      {locataire.prenom} {locataire.nom} — {locataire.adresse_logement}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Type de facture *</Label>
              <Select value={form.type} onValueChange={(value) => set('type', value)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="eau">Eau</SelectItem>
                  <SelectItem value="electricite">Électricité</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Statut</Label>
              <Select value={form.statut} onValueChange={(value) => set('statut', value)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="a_payer">À payer</SelectItem>
                  <SelectItem value="payee">Déjà payée</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Montant *</Label>
              <Input className="mt-1.5" type="number" min="0.01" step="0.01" value={form.montant} onChange={(event) => set('montant', event.target.value)} />
            </div>

            <div>
              <Label>Devise</Label>
              <Select value={form.devise} onValueChange={(value) => set('devise', value)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="CDF">CDF</SelectItem>
                  <SelectItem value="XOF">XOF</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Période *</Label>
              <Input className="mt-1.5" type="month" value={form.periode} onChange={(event) => set('periode', event.target.value)} />
            </div>

            <div>
              <Label>Date d’échéance *</Label>
              <Input className="mt-1.5" type="date" value={form.date_echeance} onChange={(event) => set('date_echeance', event.target.value)} />
            </div>

            <div className="sm:col-span-2">
              <Label>Référence de la facture</Label>
              <Input className="mt-1.5" value={form.reference} onChange={(event) => set('reference', event.target.value)} placeholder="Ex. SNEL-2026-001" />
            </div>

            <div className="sm:col-span-2">
              <Label>Note</Label>
              <Textarea className="mt-1.5" value={form.note} onChange={(event) => set('note', event.target.value)} placeholder="Informations complémentaires…" />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button type="submit" disabled={saving} className="bg-[#1677FF] hover:bg-[#0E62DB]">
              {saving ? 'Enregistrement…' : 'Créer la facture'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function BailleurPaiements() {
  const [paiements, setPaiements] = useState([]);
  const [factures, setFactures] = useState([]);
  const [locataires, setLocataires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [paiementsResponse, facturesResponse, locatairesResponse] = await Promise.all([
        bailleurApi.listPaiements(),
        bailleurApi.listFacturesServices(),
        bailleurApi.listLocataires(),
      ]);
      setPaiements(paiementsResponse.data || []);
      setFactures(facturesResponse.data || []);
      setLocataires(locatairesResponse.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Impossible de charger les paiements et factures.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadReceipt = async (paiement) => {
    try {
      await downloadPdfWithAuth(bailleurApi.recuUrl(paiement.id), `${paiement.recu_numero}.pdf`);
    } catch {
      toast.error('Téléchargement impossible');
    }
  };

  const markPaid = async (facture) => {
    setUpdatingId(facture.id);
    try {
      await bailleurApi.updateFactureServiceStatus(facture.id, 'payee');
      toast.success('Facture marquée comme payée.');
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Mise à jour impossible.');
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) {
    return (
      <AppLayout links={BAILLEUR_LINKS} testid={TID.bailleurSidebar} title="Paiements et factures">
        <Loader variant="full" />
      </AppLayout>
    );
  }

  return (
    <AppLayout links={BAILLEUR_LINKS} testid={TID.bailleurSidebar} title="Paiements et factures">
      <section className="mb-8">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-extrabold text-[#0D1B2A]">Factures eau et électricité</h2>
            <p className="mt-1 text-sm text-slate-500">Créez les factures des locataires et suivez leur règlement.</p>
          </div>
          <FactureDialog locataires={locataires} onCreated={load} />
        </div>

        {locataires.length === 0 && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            Ajoutez d’abord un locataire avant de créer une facture.
          </div>
        )}

        <div className="lb-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr className="text-left">
                  {['Type', 'Locataire', 'Période', 'Référence', 'Échéance', 'Montant', 'Statut', ''].map((heading) => (
                    <th key={heading} className="px-4 py-3 text-[11px] font-semibold uppercase tracking-widest text-slate-500">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {factures.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">Aucune facture d’eau ou d’électricité.</td></tr>
                )}
                {factures.map((facture) => {
                  const water = facture.type === 'eau';
                  return (
                    <tr key={facture.id} className="border-b border-slate-100">
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-2 font-semibold">
                          {water ? <Droplets className="h-4 w-4 text-sky-500" /> : <Zap className="h-4 w-4 text-amber-500" />}
                          {water ? 'Eau' : 'Électricité'}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-[#0D1B2A]">
                        {facture.locataire ? `${facture.locataire.prenom} ${facture.locataire.nom}` : '—'}
                      </td>
                      <td className="px-4 py-3">{facture.periode}</td>
                      <td className="px-4 py-3 font-mono text-xs">{facture.reference || '—'}</td>
                      <td className="px-4 py-3">{new Date(`${facture.date_echeance}T00:00:00`).toLocaleDateString('fr-FR')}</td>
                      <td className="px-4 py-3 font-bold text-[#0D1B2A]">{facture.montant} {facture.devise}</td>
                      <td className="px-4 py-3">
                        <Badge className={facture.statut === 'payee' ? 'bg-green-100 text-green-800 hover:bg-green-100' : 'bg-amber-100 text-amber-800 hover:bg-amber-100'}>
                          {facture.statut === 'payee' ? 'Payée' : 'À payer'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {facture.statut !== 'payee' && (
                          <Button size="sm" variant="outline" disabled={updatingId === facture.id} onClick={() => markPaid(facture)}>
                            <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Marquer payée
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section>
        <div className="mb-4">
          <h2 className="text-xl font-extrabold text-[#0D1B2A]">Historique des loyers</h2>
          <p className="mt-1 text-sm text-slate-500">Paiements de loyer et reçus générés.</p>
        </div>
        <div className="lb-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr className="text-left">
                  {['N° Reçu', 'Locataire', 'Période', 'Date', 'Montant', 'Mode', ''].map((heading) => (
                    <th key={heading} className="px-4 py-3 text-[11px] font-semibold uppercase tracking-widest text-slate-500">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paiements.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">Aucun paiement pour le moment.</td></tr>
                )}
                {paiements.map((paiement) => (
                  <tr key={paiement.id} className="border-b border-slate-100">
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{paiement.recu_numero}</td>
                    <td className="px-4 py-3 font-medium text-[#0D1B2A]">
                      {paiement.locataire ? `${paiement.locataire.prenom} ${paiement.locataire.nom}` : '—'}
                    </td>
                    <td className="px-4 py-3">{paiement.recu_periode}</td>
                    <td className="px-4 py-3">{new Date(paiement.date_paiement).toLocaleDateString('fr-FR')}</td>
                    <td className="px-4 py-3 font-bold text-[#0D1B2A]">{paiement.montant} {paiement.devise}</td>
                    <td className="px-4 py-3 text-xs uppercase text-slate-600">{paiement.mode}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => downloadReceipt(paiement)}>
                        <Download className="mr-1 h-3.5 w-3.5" /> Reçu
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </AppLayout>
  );
}
